// WhyCanadaPR.js
import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Heart, Briefcase, GraduationCap, Shield, TrendingUp } from 'lucide-react';

const WhyCanadaPR = () => {
  const benefits = [
    {
      icon: Globe,
      title: 'Welcoming Immigration Policy',
      description: 'Canada aims to welcome over 500,000 new permanent residents annually, with clear pathways and transparent processes.'
    },
    {
      icon: Heart,
      title: 'High Quality of Life',
      description: 'Consistently ranked among the top countries for quality of life, with excellent healthcare, education, and social services.'
    },
    {
      icon: Briefcase,
      title: 'Strong Economy',
      description: 'Robust job market with opportunities across various sectors, competitive salaries, and worker protections.'
    },
    {
      icon: GraduationCap,
      title: 'World-Class Education',
      description: 'Access to top-ranked universities and free public education for children of permanent residents.'
    },
    {
      icon: Shield,
      title: 'Social Benefits',
      description: 'Universal healthcare, social security, and comprehensive support systems for residents and families.'
    },
    {
      icon: TrendingUp,
      title: 'Path to Citizenship',
      description: 'Permanent residents can apply for Canadian citizenship after meeting residency requirements (typically 3 years).'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Why Choose Canada for Permanent Residency?
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Canada offers one of the world's most attractive permanent residency programs, combining economic opportunity with exceptional quality of life.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#F5F8FA] p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#202356] to-[#2542D4] rounded-lg flex items-center justify-center mb-4">
                  <Icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#202356] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed text-center">
            <strong>Did you know?</strong> Canada's permanent residents enjoy nearly all the same rights as Canadian citizens, including access to healthcare, education, and the ability to live, work, or study anywhere in Canada.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyCanadaPR;
